import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LIST_WIDGETS } from '../../http/apollo-queries';
import { Loading, Button } from '../../components/UI';
import { Modal } from '../../components/Modal';
import { useAuth } from '../../context/Auth';
import {
  IMembershipPlan,
  IMembershipPlanPrice,
  IWidget,
} from '../../types/model';
import PlanCard from './PlanCard';
import PlanFeatures from './PlanFeatues';
import { useSession } from '../../context/Session';
import { trackEvent } from '../../helpers/analytics';
import { useMembership } from '../../context/Membership';
import { useWindowSize } from '../../hooks/useWindowSize';

type Step = 'plans' | 'features' | 'order';

const ChoosePlan = () => {
  const { t } = useTranslation();
  const { breakpoint } = useWindowSize();
  const navigate = useNavigate();
  const { user, refetch: refetchUser } = useAuth();
  const session = useSession();
  const {
    membershipPlans,
    membershipPlansLoading,
    setSubscriptionChosen,
    initChargebee,
    handleChargebeeCheckout,
    inCheckout,
  } = useMembership();

  initChargebee();

  const [renewal, setRenewal] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [step, setStep] = useState<{
    type: Step;
    membershipPlan?: IMembershipPlan;
    membershipPlanPrice?: IMembershipPlanPrice;
  }>({
    type: 'plans',
  });

  const {
    data: { listWidgets: { items: widgets = [] } = {} } = {},
    loading: listWidgetsLoading = {},
  } = useQuery<{
    listWidgets: { items: IWidget[] };
  }>(LIST_WIDGETS);

  const initialLoading = membershipPlansLoading || listWidgetsLoading;

  const activeBtnClassName = 'bg-white md:bg-black text-black md:text-white';

  const swagBag =
    membershipPlans?.find(({ title }) => title === 'Arthur')?.features
      ?.swagBag || [];

  const onCheckoutComplete = () => {
    return setSubscriptionChosen()
      .then(refetchUser)
      .then(() => {
        trackEvent('subscription_complete', {
          subscription_sku: step.membershipPlan!.sku,
          subscription_renewal: step.membershipPlanPrice!.renewal,
          subscription_price: step.membershipPlanPrice!.price,
          subscription_priceSku: step.membershipPlanPrice!.sku,
          subscription_id: session.getSessionItem('cbId'),
        });
        setIsLoading(false);
        navigate('/welcome');
      });
  };

  const onCheckoutError = () => {
    setIsLoading(false);
    navigate('/checkout-error');
  };

  const handleChangeRenewal = (renewal: 'MONTHLY' | 'YEARLY') => {
    if (step?.membershipPlan) {
      setStep({
        ...step,
        membershipPlanPrice: step.membershipPlan.prices.find(
          (p) => p.renewal === renewal
        ),
      });
    }
    return setRenewal(renewal);
  };

  const handleCheckout = () => {
    setIsLoading(true);

    if (step.membershipPlan?.sku === 'Camelot') {
      onCheckoutComplete().then();
      return;
    }

    // console.log('cbInstance', cbInstance);
    handleChargebeeCheckout(
      user!.email,
      step.membershipPlan!.id,
      step.membershipPlanPrice!.sku,
      onCheckoutComplete,
      onCheckoutError
    );
  };

  return (
    <Modal
      isOpen={true}
      height={'full'}
      padding={!breakpoint ? 'none' : 'base'}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      isTransparent={!breakpoint}
      isFullScreenElement={!breakpoint}
      className={'w-full md:w-180 bg-black md:bg-white border-none pt-16'}
    >
      <div
        className={
          'h-full px-4 pb-4 overflow-y-auto text-center items-center justify-center '
        }
      >
        <h1 className="text-xl font-semibold text-white md:text-black ">
          {t('choosePlan.title')}
        </h1>
        <div className="p-4 text-md text-grey-50">
          {t('choosePlan.description')}
        </div>
        {initialLoading ? (
          <Loading status className={'mx-auto text-white md:text-black'} />
        ) : (
          <>
            <div className="pb-4 inline-flex gap-4 text-lg mx-auto">
              <Button
                buttonClass={
                  renewal === 'YEARLY'
                    ? activeBtnClassName
                    : 'text-white md:text-black'
                }
                onClick={() => handleChangeRenewal('YEARLY')}
              >
                {t('checkout.renewal_YEARLY')}
              </Button>
              <Button
                buttonClass={
                  renewal === 'MONTHLY'
                    ? activeBtnClassName
                    : 'text-white md:text-black'
                }
                onClick={() => handleChangeRenewal('MONTHLY')}
              >
                {t('checkout.renewal_MONTHLY')}
              </Button>
            </div>
            {step.type === 'features' ? (
              <div>
                <PlanFeatures
                  membershipPlan={step.membershipPlan!}
                  widgets={widgets}
                  renewal={renewal}
                  swagBag={swagBag}
                />
                <div className="flex justify-between w-full">
                  <Button
                    onClick={() => setStep({ type: 'plans' })}
                    disabled={inCheckout}
                    buttonClass={'text-white md:text-black'}
                  >
                    {t('choosePlan.back')}
                  </Button>
                  <Button
                    onClick={handleCheckout}
                    variant="primary"
                    disabled={inCheckout}
                    loading={isLoading}
                  >
                    {step.membershipPlan?.sku !== 'Camelot'
                      ? t('choosePlan.purchase')
                      : t('choosePlan.continue')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {membershipPlans.map((membershipPlan) => (
                  <PlanCard
                    key={membershipPlan.id}
                    membershipPlan={membershipPlan}
                    onSelect={() =>
                      setStep({
                        type: 'features',
                        membershipPlan: membershipPlan,
                        membershipPlanPrice: membershipPlan.prices.find(
                          (p) => p.renewal === renewal
                        ),
                      })
                    }
                    renewal={renewal}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ChoosePlan;
